import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Popover } from "react-tiny-popover";
import { useStore } from "../../../../stores";
import * as yup from "yup";
import { collect } from "collect.js";
import {
  CannedResponse,
  DefaultCategoryName,
  UserType,
  WhatsappFlow,
  WhatsappTemplate,
} from "../../../../models";
import { Collapse, Input } from "antd";
import { BsSearch } from "react-icons/bs";
import { MdOutlineQuickreply } from "react-icons/md";
import {
  SYSTEM_PREDEIFINED_FIELDS,
  extractVariablesFromString,
} from "../../../campaigns/components/tabs/UploadAudience";
import { AntSelect, Field, Modal } from "../../../../components";
import { FieldArray, Formik } from "formik";
import locale from "../../../../constants/locale";
import { useTranslation } from "react-i18next";
import WhatsappApp from "../../../campaigns/twilioWhatsappTemplates/components/WhatsappApp";
import WhatsAppPreview from "../../../campaigns/metaWhatsappFlows/components/WhatsAppPreview";
import { convertFlowDataToResponseFormat } from "../../../../utils/whatsappFlowHelper";

export default function QuickReplyPopover({
  hideQuickReplies,
  onSelectQuickReply,
  isWhatsappChannel,
}: {
  hideQuickReplies?: boolean;
  onSelectQuickReply: (f: CannedResponse) => void;
  isWhatsappChannel?: boolean;
}) {
  const { t } = useTranslation();
  const { selectedBot } = useStore("bots");
  const { selectedChat } = useStore("chats");
  const [fields, setFields] = useState<any[]>([]);
  const [selectedQuickReply, setSelectedQuickReply] =
    useState<CannedResponse>();
  const [selectedWaFlow, setSelectedWaFlow] = useState<any>();
  const [activeKey, setActiveKey] = useState<string | string[] | undefined>(
    undefined
  );
  const [waFlowActiveScreenId, setWaFlowActiveScreenId] = useState<string>();
  const [waFlowDefaultValues, setWaFlowDefaultValues] = useState<any>({});
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { cannedResponses, loadCannedResponses, templates, loadTemplates } =
    useStore("bots");
  const { flows, loadFlows } = useStore("whatsappFlows");
  const [search, setSearch] = useState<string | undefined>();
  const { getAssignedGroupIdsOfUser } = useStore("users");
  const { user } = useStore("auth");

  const accessibleQuickReplies = useMemo(() => {
    if (!selectedBot?.id || !user) return [];
    return cannedResponses.filter((f) => {
      if (hideQuickReplies) return false;
      if (user.type !== UserType.Collaborator) return true;
      return getAssignedGroupIdsOfUser(user, selectedBot.id).some((s) =>
        f.groupIds?.includes(s)
      );
    });
  }, [
    selectedBot?.id,
    user,
    cannedResponses,
    hideQuickReplies,
    getAssignedGroupIdsOfUser,
  ]);

  const categories = useMemo(
    () =>
      collect([
        ...accessibleQuickReplies,
        ...collect(templates)
          .map((template) => ({
            id: template.id,
            category: t(locale.templates),
            content: template.name,
            media: [
              {
                name: template.name,
                type: "contentTemplate",
                meta: {
                  template,
                  id: template.id,
                  twilioContentSid: template.twilioContentSid,
                  message: template.messageBody,
                  meta: template.meta,
                  contentType: template.contentType,
                },
              },
            ],
          }))
          .toArray(),
        ...collect(isWhatsappChannel ? flows : [])
          .filter((f) => f.status === "published")
          .map((flow) => ({
            id: flow.id,
            category: t(locale.metaWhatsappFlows),
            content: flow.name,
            media: [
              {
                name: flow.name,
                type: "waFlow",
                meta: {
                  flow,
                  flowId: flow.id,
                  flowName: flow.name,
                },
              },
            ],
          })),
      ] as CannedResponse[])
        .map((f) => ({ ...f, category: f.category ?? DefaultCategoryName }))
        .filter((f) => {
          if (!search) return true;
          return f.content?.toLowerCase().includes(search.toLowerCase());
        })
        .groupBy("category")
        .all() as any,
    [accessibleQuickReplies, search, templates, flows, isWhatsappChannel, t]
  ) as Record<string, CannedResponse[]>;

  const canLoad = !!(selectedBot && !cannedResponses.length);

  useEffect(() => {
    if (canLoad) {
      loadCannedResponses();
    }
  }, [loadCannedResponses, canLoad]);

  useEffect(() => {
    loadTemplates(selectedBot?.settings?.whatsappProvider === "meta", true);
    loadFlows();
  }, [loadTemplates, loadFlows, selectedBot?.settings?.whatsappProvider]);

  const contactsTableHeader = useMemo(
    () =>
      selectedBot?.customization?.contactsTable.header?.filter(
        (f) =>
          ![
            "firstName",
            "lastName",
            "channel",
            "createdAt",
            "lastMessage",
          ].includes(f)
      ) || [],
    [selectedBot?.customization?.contactsTable.header]
  );

  const buildWaFlowDefaultValues = (flow: WhatsappFlow) => {
    const formFields = flow.flow?.screens
      ?.flatMap((screen) =>
        screen.layout.children.flatMap((form) => form.children)
      )
      .filter(
        (f: any) =>
          ["TextInput", "TextArea"].includes(f.type) &&
          f?.customMeta?.suggestedValue
      );

    const defaultValues = formFields?.reduce(
      (acc, field: any) => ({
        ...acc,
        [field.name]: selectedChat?.data?.[field.customMeta.suggestedValue],
      }),
      {}
    );

    return defaultValues;
  };

  const handleSelect = (f: CannedResponse) => {
    if (f.media?.[0]?.type === "contentTemplate") {
      const template = f.media[0].meta.template as WhatsappTemplate;
      const fields = extractVariablesFromString(template.messageBody).map(
        (f) => ({ source: f, value: undefined })
      );
      if (fields?.length) {
        setSelectedQuickReply({
          ...f,
          media: [
            {
              ...f.media[0],
              meta: {
                template,
                id: template.id,
                twilioContentSid: template.twilioContentSid,
                message: template.messageBody,
                fieldsMapping: fields,
                meta: template.meta,
                contentType: template.contentType,
              },
            },
          ],
        });
        setFields(fields);
        onClose();
        return;
      }
    }
    if (f.media?.[0]?.type === "waFlow") {
      const flowData = f.media[0].meta.flow as WhatsappFlow;
      setSelectedWaFlow(f);
      if (flowData.type !== "customized") {
        setWaFlowDefaultValues(buildWaFlowDefaultValues(flowData));
        setWaFlowActiveScreenId(flowData.flow?.screens?.[0]?.id);
      } else {
        setWaFlowDefaultValues({});
        setWaFlowActiveScreenId(undefined);
      }
      onClose();
      return;
    }
    onSelectQuickReply(f);
    onClose();
  };

  const onClose = () => {
    setSearch(undefined);
    setActiveKey(undefined);
    setPopoverOpen(false);
  };

  return (
    <>
      <Popover
        isOpen={popoverOpen}
        positions={["top", "right"]}
        align={"start"}
        containerStyle={{ zIndex: "100000000", width: "300px" }}
        padding={7}
        onClickOutside={onClose}
        content={() => (
          <div className="has-border has-background-white has-rounded-corners">
            <Input
              prefix={<BsSearch />}
              bordered={false}
              placeholder={t(locale.searchQuickReplies)}
              className="has-border-bottom"
              onChange={(e) => setSearch(e.target.value)}
              value={search}
            />
            {Object.keys(categories).length === 0 && (
              <h2 className="py-2 px-4">{t(locale.noQuickRepliesFound)}</h2>
            )}
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              <Collapse
                ghost
                rootClassName="quick-reply-popover"
                activeKey={search ? Object.keys(categories) : activeKey}
                onChange={setActiveKey}
                items={Object.keys(categories)?.map((f) => ({
                  key: f,
                  label: f,
                  children: (
                    <QuickReplies
                      onSelect={handleSelect}
                      quickReplies={categories[f]}
                    />
                  ),
                }))}
              />
            </div>
          </div>
        )}
      >
        <button
          className={clsx("button has-text-primary", {
            "is-ghost is-no-box-shadow": true,
          })}
          onClick={() => setPopoverOpen(true)}
        >
          <span style={{ position: "absolute" }} className="icon">
            <MdOutlineQuickreply color="#959595" />
          </span>
        </button>
      </Popover>
      {selectedQuickReply && (
        <Formik
          initialValues={{ fields }}
          onSubmit={(values) => {
            if (!selectedQuickReply?.media?.[0]) return;
            onSelectQuickReply({
              ...selectedQuickReply,
              media: [
                {
                  ...selectedQuickReply?.media[0],
                  meta: {
                    ...selectedQuickReply?.media[0]?.meta,
                    fieldsMapping: values.fields,
                  },
                },
              ],
            });
            setSelectedQuickReply(undefined);
          }}
        >
          {({ isSubmitting, submitForm, values }) => (
            <Modal
              isOpen
              title={"Fields Mapping"}
              onClose={() => setSelectedQuickReply(undefined)}
              primaryText={t(locale.save)}
              primaryLoading={isSubmitting}
              onPrimary={isSubmitting ? undefined : submitForm}
              isWideScreenH
            >
              <div className="columns">
                <div className="column is-6">
                  <FieldArray
                    name="fields"
                    render={() => (
                      <div>
                        {fields.map((field: any, index: number) => (
                          <div key={index} className="columns">
                            <div className="column is-half">
                              <Field
                                name={`fields.${index}.source`}
                                readOnly={true}
                              />
                            </div>
                            <div className="column is-half">
                              <Field
                                name={`fields.${index}.value`}
                                options={[
                                  ...SYSTEM_PREDEIFINED_FIELDS,
                                  ...contactsTableHeader,
                                ]}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </div>
                <div className="column is-6">
                  <WhatsappApp
                    isMobile={true}
                    contentType={
                      selectedQuickReply?.media?.[0]?.meta?.contentType
                    }
                    meta={selectedQuickReply?.media?.[0]?.meta?.meta}
                    message={selectedQuickReply?.media?.[0]?.meta?.message}
                  />
                </div>
              </div>
            </Modal>
          )}
        </Formik>
      )}
      {selectedWaFlow && (
        <Formik
          initialValues={{
            type: "waFlow",
            buttonLabel: undefined,
            message: undefined,
            screenTitle: undefined,
          }}
          validationSchema={yup.object({
            type: yup.string().required(t(locale.fieldRequired)),
            buttonLabel: yup.string().required(t(locale.fieldRequired)),
            message: yup.string().required(t(locale.fieldRequired)),
            screenTitle: yup.string().when("type", {
              is: "waResponse",
              then: yup.string().required(t(locale.fieldRequired)),
            }),
          })}
          onSubmit={(values) => {
            if (!selectedWaFlow?.media?.[0]) return;
            onSelectQuickReply({
              ...selectedWaFlow,
              media: [
                {
                  ...selectedWaFlow?.media[0],
                  type: values.type,
                  meta: {
                    ...selectedWaFlow?.media[0]?.meta,
                    ...values,
                  },
                },
              ],
            });
            setSelectedWaFlow(undefined);
          }}
        >
          {({ isSubmitting, submitForm, values, setFieldValue }) => {
            const isCustomizedFlow =
              (selectedWaFlow?.media?.[0]?.meta?.flow as WhatsappFlow)?.type === "customized";
            return (
            <Modal
              isOpen
              title={"WhatsApp Flow"}
              onClose={() => setSelectedWaFlow(undefined)}
              primaryText={
                values.type === "waFlow" ? t(locale.save) : undefined
              }
              primaryLoading={isSubmitting}
              onPrimary={isSubmitting ? undefined : submitForm}
              isWideScreenH
            >
              <Field name="responseMeta" type="hidden" />
              <div className="columns">
                <div className={isCustomizedFlow ? "column" : "column is-6"}>
                  <AntSelect
                    name="type"
                    label={t(locale.type)}
                    className="custom-select"
                    options={[
                      {
                        label: "Flow",
                        value: "waFlow",
                      },
                      ...(!isCustomizedFlow
                        ? [
                            {
                              label: "Response",
                              value: "waResponse",
                            },
                          ]
                        : []),
                    ]}
                    extras={
                      values.type === "waResponse" ? (
                        <p className="has-text-grey is-size-7">
                          In this type you have to submit the flow manualy from
                          "Preview Flow" button and the user will receive the
                          message with the details.
                        </p>
                      ) : undefined
                    }
                  />
                  {values.type === "waResponse" && (
                    <Field
                      name="screenTitle"
                      label={t(locale.screenTitle)}
                      placeholder={t(locale.screenTitlePlaceholder)}
                      className="custom-input"
                    />
                  )}
                  <Field
                    name="buttonLabel"
                    label={t(locale.buttonLabel)}
                    placeholder={t(locale.buttonLabelPlaceholder)}
                    className="custom-input"
                  />
                  <Field
                    name="message"
                    label={t(locale.message)}
                    placeholder={t(locale.messagePlaceholder)}
                    useTextArea
                    className="custom-input"
                    inputProps={{
                      maxLength: 1024,
                      style: { height: 135, resize: "none" },
                    }}
                  />
                </div>
                {!isCustomizedFlow && (
                  <div className="column is-6">
                    {waFlowActiveScreenId && (
                      <WhatsAppPreview
                        isDynamic={values.type === "waResponse"}
                        isFullMode
                        formData={
                          selectedWaFlow?.media[0]?.meta?.flow as WhatsappFlow
                        }
                        defaultFormValues={
                          values.type === "waResponse" ? waFlowDefaultValues : {}
                        }
                        activeScreenId={waFlowActiveScreenId}
                        onScreenChange={setWaFlowActiveScreenId}
                        onCompleted={(values) => {
                          if (!selectedWaFlow?.media?.[0]) return;
                          setFieldValue(
                            "responseConfig",
                            convertFlowDataToResponseFormat(
                              selectedWaFlow?.media[0]?.meta
                                ?.flow as WhatsappFlow,
                              values
                            )
                          );
                          submitForm();
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </Modal>
          );
          }}
        </Formik>
      )}
    </>
  );
}

const QuickReplies = ({
  quickReplies,
  onSelect,
}: {
  quickReplies: CannedResponse[];
  onSelect: (f: CannedResponse) => void;
}) => {
  return (
    <div>
      {quickReplies?.map((f) => (
        <div
          className="py-2 px-4 is-clickable has-border-bottom my-2"
          onClick={() => onSelect(f)}
        >
          <h2 className="line-clamp-2">{f.content}</h2>
        </div>
      ))}
    </div>
  );
};
